import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type RSVP from 'rsvp';
import { allSettled } from 'rsvp';

import type { PopupData, Service } from 'webapp-types';

import BoardUnusedMetricsComponent from 'webapp/components/board/unused-metrics';
import CommonPopupsConfirmComponent from 'webapp/components/common/popups/confirm';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import type { Args } from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

export interface SelectedForDeletion {
	customQueries: number[];
	scripts: number[];
}

export default class BoardPopupDeleteConfirmComponent extends CommonPopupsConfirmComponent<PopupData.Board.ConfirmDelete> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	customQueriesToDelete: number[] = [];
	scriptsToDelete: number[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void this.mmMetricsService.requestMetrics();
	}

	get boardId() {
		return this.popupData.boardId;
	}

	override confirmAndClose() {
		void this.deleteUnusedCustomQueriesAndScripts().then(() => {
			this.popupData.onConfirm();
			this.close();
		});
	}

	@action
	changeSelectedItems(selectedItems: SelectedForDeletion): void {
		this.customQueriesToDelete = selectedItems.customQueries;
		this.scriptsToDelete = selectedItems.scripts;
	}

	private deleteUnusedCustomQueriesAndScripts(): Promise<void> {
		const promises: Promise<void>[] = [];

		let finalPromise;

		this.customQueriesToDelete.forEach((id) => {
			promises.push(
				this.requestService.api('DELETE', `/customqueries/${id}`, null, {
					skipShowingErrorInfolayer: true,
				}),
			);
		});

		this.scriptsToDelete.forEach((id) => {
			promises.push(
				this.requestService.api('DELETE', `/scripts/${id}`, null, {
					skipShowingErrorInfolayer: true,
				}),
			);
		});

		if (promises.length) {
			this.submitInProgress = true;

			finalPromise = allSettled(promises).then((statusArray) => {
				const rejectedRequests = statusArray.filter(
					(item) => item.state === ('rejected' as RSVP.State),
				);

				if (rejectedRequests.length) {
					this.infolayerService.error(
						'There was a problem deleting some of your selected metrics.',
					);
				} else {
					this.infolayerService.ok('Selected metrics have been deleted!');
				}

				this.submitInProgress = false;
			});
		} else {
			finalPromise = Promise.resolve();
		}

		return finalPromise;
	}
	<template>
		<div class="content">
			<div class="header red-on-transparent">
				<CommonUiIconComponent @name="AlertFill" @size="lg" />
			</div>
			<span class="title">{{{this.title}}}</span>
			<div class="row">
				<p>
					{{{this.text}}}
					{{#if this.confirmWord}}
						<span>Type {{this.confirmWord}} to confirm:</span>
					{{/if}}
				</p>
			</div>
			{{#if this.confirmWord}}
				<div class="row">
					<FormInputComponent
						name="confirmword"
						maxlength="50"
						@value={{this.typedWord}}
						@insertNewline={{this.confirm}}
					/>
					<RowErrorComponent @items={{this.errors.typedWord}} />
				</div>
			{{/if}}
			<BoardUnusedMetricsComponent
				@boardId={{this.boardId}}
				@onChangeSelectedItems={{this.changeSelectedItems}}
				@onRecenter={{this.scheduleReposition}}
			/>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text={{this.cancelAction}}
						@theme="grey-empty"
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text={{this.confirmAction}}
						@extraClass="save"
						@theme="red"
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.confirm}}
					/>
				</div>
			</div>
		</div>
	</template>
}
